import { useEffect, useRef, useState } from 'react';
import { Maximize2, Mic, MicOff, Minimize2, PhoneOff, RefreshCw, Video as VideoIcon, VideoOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MediaVideo = ({ stream, muted = false, className }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.srcObject = stream || null; }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={className} />;
};

const MediaAudio = ({ stream }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.srcObject = stream || null; }, [stream]);
  return <audio ref={ref} autoPlay aria-label="Remote call audio" className="sr-only" />;
};

export const CallSurface = ({ callState, otherPartyName }) => {
  const { call, localStream, remoteStream, connectionState, muted, cameraOff, error, acceptCall, declineCall, endCall, toggleMute, toggleCamera, switchCamera, isTerminal } = callState;
  const [elapsed, setElapsed] = useState(0);
  const [minimized, setMinimized] = useState(false);
  const [actionError, setActionError] = useState(null);
  useEffect(() => {
    if (!call || call.status !== 'accepted') return undefined;
    const acceptedAt = Date.parse(call.accepted_at || '') || Date.now();
    const update = () => setElapsed(Math.max(0, Math.floor((Date.now() - acceptedAt) / 1000)));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [call]);
  if (!call) return error ? <p role="status" className="px-4 py-2 text-xs text-amber-700">{error}</p> : null;
  const isIncoming = call.direction === 'incoming' && call.status === 'ringing';
  if (isIncoming) return (
    <section role="dialog" aria-modal="true" aria-label={`Incoming ${call.media_type} call from ${otherPartyName}`} className="absolute inset-x-3 top-3 z-20 rounded-2xl border border-brand-200 bg-white p-4 shadow-xl">
      <p className="font-semibold">{otherPartyName} is calling</p>
      <p className="mt-1 text-sm text-gray-500">{call.media_type === 'video' ? 'Video' : 'Audio'} call · {call.campaign_id ? 'Campaign conversation' : 'Conversation'}</p>
      <div className="mt-4 flex gap-2">
        <Button onClick={acceptCall} aria-label={`Accept ${call.media_type} call`}>Accept {call.media_type}</Button>
        <Button variant="outline" onClick={declineCall} aria-label="Decline call">Decline</Button>
      </div>
    </section>
  );
  if (isTerminal) return (
    <section role="status" aria-label="Call ended" className="absolute inset-x-3 top-3 z-20 rounded-2xl bg-slate-950 p-4 text-white shadow-xl">
      <p className="font-semibold">Call {call.status === 'ended' ? 'ended' : call.status}</p>
      <p className="mt-1 text-xs text-slate-300">{call.terminal_reason || 'The call is no longer active.'}</p>
    </section>
  );
  if (minimized) return (
    <section aria-label="Minimized active call" className="absolute right-3 top-3 z-20 flex items-center gap-3 rounded-full bg-slate-950 px-4 py-2 text-white shadow-xl">
      <MediaAudio stream={remoteStream} />
      <span className="text-sm font-medium">{otherPartyName} · {connectionState}</span>
      <Button variant="ghost" size="icon" className="text-white" onClick={() => setMinimized(false)} aria-label="Expand call"><Maximize2 /></Button>
      <Button variant="destructive" size="icon" onClick={endCall} aria-label="End call"><PhoneOff /></Button>
    </section>
  );
  return (
    <section aria-label="Active call" className="absolute inset-x-3 top-3 z-20 overflow-hidden rounded-2xl bg-slate-950 text-white shadow-xl">
      {call.media_type === 'audio' && <MediaAudio stream={remoteStream} />}
      {call.media_type === 'video' && <MediaVideo stream={remoteStream} className="h-48 w-full bg-slate-800 object-cover" />}
      {call.media_type === 'video' && <MediaVideo stream={localStream} muted className="absolute right-3 top-3 h-20 w-28 rounded-lg bg-slate-700 object-cover" />}
      <div className="flex items-center justify-between gap-3 p-3">
        <div><p className="font-semibold">{otherPartyName}</p><p className="text-xs text-slate-300">{connectionState} · {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</p></div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="text-white" onClick={() => setMinimized(true)} aria-label="Minimize call"><Minimize2 /></Button>
          <Button variant="ghost" size="icon" className="text-white" onClick={toggleMute} aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}>{muted ? <MicOff /> : <Mic />}</Button>
          {call.media_type === 'video' && <Button variant="ghost" size="icon" className="text-white" onClick={toggleCamera} aria-label={cameraOff ? 'Turn camera on' : 'Turn camera off'}>{cameraOff ? <VideoOff /> : <VideoIcon />}</Button>}
          {call.media_type === 'video' && <Button variant="ghost" size="icon" className="text-white" onClick={() => switchCamera().then(() => setActionError(null)).catch((cause) => setActionError(cause.message))} aria-label="Switch camera"><RefreshCw /></Button>}
          <Button variant="destructive" size="icon" onClick={endCall} aria-label="End call"><PhoneOff /></Button>
        </div>
      </div>
      <p className="px-3 pb-2 text-[11px] text-slate-400">Speaker output follows your browser or device audio setting.</p>
      {(error || actionError) && <p role="status" className="px-3 pb-3 text-xs text-amber-300">{actionError || error}</p>}
    </section>
  );
};

export default CallSurface;
